import { CURRENCY_CODE_LIST } from '../config/constants';

export const getCurrencySymbol = (currencyCode) => {
  const currency = CURRENCY_CODE_LIST.find(c => c.id === currencyCode);
  return currency ? currency.symbol : currencyCode; // Fallback to currency code if symbol not found
};

export const parseDecimal128 = (price) => {
  if (price == null) return 0;

  let numericPrice;
  if (typeof price === "object" && price.$numberDecimal) {
    numericPrice = parseFloat(price.$numberDecimal);
  } else if (typeof price === "string") {
    numericPrice = parseFloat(price);
  } else if (typeof price === "number") {
    numericPrice = price;
  } else {
    throw new Error(`Invalid price format: expected number, string, or Decimal128, got ${typeof price}`);
  }

  if (isNaN(numericPrice)) {
    throw new Error("Price is not a valid number");
  }

  if (numericPrice < 0) {
    throw new Error("Price cannot be negative");
  }

  return numericPrice;
};

export const formatPriceWithCurrency = (price, currencyCode, showCode = false) => {
  const numericPrice = parseDecimal128(price);

  let formattedAmount;
  try {
    // Intl handles thousands grouping, per-currency decimal counts (JPY = 0,
    // KWD/BHD = 3), and correct placement/spacing of non-Latin/RTL symbols.
    // 'narrowSymbol' keeps the familiar short glyph ($, €, £) where one exists
    // and falls back to the ISO code with a space (e.g. "AED 1,234.50") where
    // it doesn't. Locale is pinned to 'en-US' for deterministic, test-stable
    // output independent of the viewer's browser locale.
    formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
    }).format(numericPrice);
  } catch (err) {
    // Intl.NumberFormat throws RangeError for an unknown/invalid ISO code.
    // Preserve the legacy behavior (symbol lookup, or raw code if unknown) but
    // keep thousands grouping so the fallback still looks correct.
    const symbol = getCurrencySymbol(currencyCode);
    const groupedAmount = numericPrice.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    formattedAmount = `${symbol}${groupedAmount}`;
  }

  return showCode ? `${formattedAmount} ${currencyCode}` : formattedAmount;
};

// Compact display for dashboard stat cards. No cents; k-suffix above 10k, M above 1M.
// Use formatPriceWithCurrency for full-precision contexts (line items, totals, receipts).
export const formatCompactCurrency = (price, currencyCode) => {
  const numericPrice = parseDecimal128(price);
  const symbol = getCurrencySymbol(currencyCode);

  if (numericPrice >= 1_000_000) {
    return `${symbol}${(numericPrice / 1_000_000).toFixed(1)}M`;
  }
  if (numericPrice >= 10_000) {
    return `${symbol}${(numericPrice / 1_000).toFixed(1)}k`;
  }
  const rounded = Math.round(numericPrice);
  const formatted = new Intl.NumberFormat('en-US').format(rounded);
  return `${symbol}${formatted}`;
};

export const parsePrice = (price) => {
  return parseDecimal128(price);
};
